import React from 'react';
import { ArrowLeft, Calendar, MapPin, TrendingUp, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

const InPostIberiaArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Industry Highlights
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-green-500">
              <TrendingUp className="h-3 w-3 mr-1" />
              {t('article.inpost.badge')}
            </Badge>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              {t('article.inpost.date')}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />
              {t('article.inpost.location')}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('article.inpost.title')}
          </h1>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('article.inpost.summary')}
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src="https://cdn-trans.info/uploads/2022/12/9da2d6974e5613e85589fccf9c4.jpg"
            alt={t('article.inpost.imageCaption')}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t('article.inpost.imageCaption')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlight Box */}
            <Card className="border-l-4 border-l-green-500 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <Package className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-green-800 mb-2">Key Highlight</h3>
                    <p className="text-green-700">{t('article.inpost.highlight')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Sections */}
            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.inpost.section1.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.inpost.section1.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.inpost.section2.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.inpost.section2.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.inpost.section3.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.inpost.section3.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.inpost.section4.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.inpost.section4.content')}
                </p>
              </div>
            </div>

            {/* Impact Analysis */}
            <Card className="bg-gradient-to-r from-green-50 to-teal-50 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center text-green-800">
                  <Package className="h-5 w-5 mr-2" />
                  {t('article.inpost.impact.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-green-700">
                  {t('article.inpost.impact.content')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Company</span>
                  <span className="font-semibold">InPost</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Daily Capacity</span>
                  <span className="font-semibold text-green-600">500,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Lockers</span>
                  <span className="font-semibold">~4,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge className="bg-green-500">Network Expansion</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  EcommerceNews.eu
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://ecommercenews.eu/inpost-strengthens-operations-in-iberian-peninsula/', '_blank')}
                >
                  Read Original Article
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InPostIberiaArticle;